import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { SourceCitation as SourceCitationType } from '../../types';
import { isSafeUrl } from '../../utils/urlSafety';

interface SourceCitationProps {
  source: SourceCitationType;
  index: number;
}

function safeHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

const SourceCitation = React.memo(function SourceCitation({ source, index }: SourceCitationProps) {
  const safe = source.url ? isSafeUrl(source.url) : false;
  const hostname = safe ? safeHostname(source.url) : null;
  const href = safe ? source.url : null;
  const baseClassName =
    'flex items-start gap-2 p-2 bg-gray-50 rounded border border-gray-200 text-xs';
  const className = href
    ? `${baseClassName} hover:border-blue-300 hover:bg-blue-50 transition-colors`
    : baseClassName;

  const content = (
    <>
      <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
        {index}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 truncate">{source.title}</div>
        {source.snippet && (
          <div className="text-gray-600 text-xs mt-0.5 line-clamp-2">{source.snippet}</div>
        )}
        {hostname && (
          <div className="flex items-center gap-1 text-blue-600 mt-1">
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
            <span className="truncate">{hostname}</span>
          </div>
        )}
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${source.title} (opens in new tab)`}
        className={className}
      >
        {content}
      </a>
    );
  }

  return (
    <div role="note" aria-label={source.title} className={className}>
      {content}
    </div>
  );
});

export default SourceCitation;
